<script setup lang="ts">
/**
 * Condition builder: rules („Status ist Offen“) combined in AND/OR groups that
 * can nest up to `maxDepth`. Each group is a card with a coloured rail (brand
 * for AND, violet for OR) and an AND/OR switch; rules can be added, duplicated
 * and removed, groups too.
 *
 * The caller owns the vocabulary: `fields` (key, label, optional type and
 * group) and `operators(field)` (key, label, arity 0/1/2/many). Field and
 * operator are kit dropdowns: typing filters the list, `field.group` heads
 * the field list, and the operator stays disabled until a field is chosen.
 * The value of a rule is edited in the `#value` slot (`{ rule, field,
 * operator, update }`); without it a text field (two for a range, a comma
 * list for many) stands in.
 * `v-model` is the root group — every change hands back a new tree.
 * `describeCondition` turns a tree into one readable line.
 */
import { computed, provide, useSlots, type Slot } from 'vue';
import NxConditionGroupNode from './NxConditionGroupNode.vue';
import { conditionBuilderKey, type NxConditionBuilderLabels } from './context';
import {
  createConditionGroup,
  type NxConditionField,
  type NxConditionGroup,
  type NxConditionOperator,
  type NxConditionOperators,
  type NxConditionRule,
} from './model';

const props = withDefaults(
  defineProps<{
    fields: readonly NxConditionField[];
    operators: NxConditionOperators;
    /** Nesting levels including the root group. Defaults to 3. */
    maxDepth?: number;
    disabled?: boolean;
    /** Accessible name of the whole builder. */
    ariaLabel?: string;
    labels?: Partial<NxConditionBuilderLabels>;
  }>(),
  { maxDepth: 3, disabled: false, ariaLabel: 'Bedingungen', labels: () => ({}) }
);

const model = defineModel<NxConditionGroup>({ default: () => createConditionGroup() });

defineSlots<{
  value?: (props: {
    rule: NxConditionRule;
    field: NxConditionField | undefined;
    operator: NxConditionOperator | undefined;
    update: (value: unknown) => void;
  }) => unknown;
}>();

const DEFAULT_LABELS: NxConditionBuilderLabels = {
  and: 'UND',
  or: 'ODER',
  combinator: 'Verknüpfung',
  addRule: 'Bedingung',
  addGroup: 'Gruppe',
  remove: 'Entfernen',
  duplicate: 'Duplizieren',
  field: 'Feld',
  fieldPlaceholder: 'Feld wählen',
  operator: 'Operator',
  value: 'Wert',
  valueFrom: 'von',
  valueTo: 'bis',
  valueList: 'Werte, mit Komma getrennt',
  empty: 'Noch keine Bedingung — alle Einträge passen.',
  rule: 'Bedingung',
  group: 'Gruppe',
};

const slots = useSlots();

provide(conditionBuilderKey, {
  fields: computed(() => props.fields),
  operators: computed(() => props.operators),
  maxDepth: computed(() => Math.max(1, props.maxDepth)),
  disabled: computed(() => props.disabled),
  labels: computed(() => ({ ...DEFAULT_LABELS, ...props.labels })),
  valueSlot: () => slots.value as Slot | undefined,
});
</script>

<template>
  <div class="nx-condition-builder" role="group" :aria-label="props.ariaLabel">
    <NxConditionGroupNode :group="model" :depth="0" @update="model = $event" />
  </div>
</template>

<style scoped>
.nx-condition-builder {
  container-type: inline-size;
  min-width: 0;
}
</style>
